import type {
  TrackableItem,
  TrackableList,
  TrackableObjectType,
} from 'types/trackables';
import type { FilesSearchResult } from 'types/files_search_result';
import type { Stream } from 'types/stream';
import type { Camera } from 'types/camera';
import type { LumeoFile } from 'types/file';

export type TrackableObjectTypeOption = {
  value: TrackableObjectType;
  label: string;
};

export const TRACKABLE_OBJECT_TYPE_OPTIONS: readonly TrackableObjectTypeOption[] = [
  { label: 'Face', value: 'face' },
  { label: 'Vehicle', value: 'vehicle' },
  { label: 'Custom', value: 'custom' },
] as const;

export const TRACKABLE_OBJECT_TYPE_LABELS: Record<TrackableObjectType, string> = {
  face: 'Face',
  vehicle: 'Vehicle',
  custom: 'Custom',
};

export type TrackableDisplay = {
  id: string;
  itemName?: string;
  listId?: string;
  listName?: string;
  inlineLabel: string;
  tooltip: string;
};

type TrackableItemLookup =
  | Map<string, Pick<TrackableItem, 'id' | 'name' | 'trackable_list_id'>>
  | Record<string, Pick<TrackableItem, 'id' | 'name' | 'trackable_list_id'>>;

type TrackableListLookup =
  | Map<string, Pick<TrackableList, 'id' | 'name'>>
  | Record<string, Pick<TrackableList, 'id' | 'name'>>;

function getItem(
  lookup: TrackableItemLookup | undefined,
  id: string
): Pick<TrackableItem, 'id' | 'name' | 'trackable_list_id'> | undefined {
  if (!lookup) {
    return undefined;
  }

  if (lookup instanceof Map) {
    return lookup.get(id);
  }

  return lookup[id];
}

function getList(
  lookup: TrackableListLookup | undefined,
  id: string | undefined
): Pick<TrackableList, 'id' | 'name'> | undefined {
  if (!lookup || !id) {
    return undefined;
  }

  if (lookup instanceof Map) {
    return lookup.get(id);
  }

  return lookup[id];
}

export function buildTrackableDisplay(
  id: string,
  item?: Pick<TrackableItem, 'id' | 'name' | 'trackable_list_id'>,
  list?: Pick<TrackableList, 'id' | 'name'>
): TrackableDisplay {
  const itemName = item?.name;
  const listName = list?.name;
  const inlineLabel =
    itemName && listName
      ? `${listName} → ${itemName}`
      : itemName ?? listName ?? id;
  const tooltip =
    itemName && listName ? `${listName} → ${itemName}` : inlineLabel;

  return {
    id,
    itemName,
    listId: item?.trackable_list_id,
    listName,
    inlineLabel,
    tooltip,
  };
}

export function getTrackableDisplays(
  trackableIds: Iterable<string>,
  itemsLookup?: TrackableItemLookup,
  listsLookup?: TrackableListLookup
): TrackableDisplay[] {
  const seen = new Set<string>();
  const result: TrackableDisplay[] = [];

  for (const id of trackableIds) {
    if (!id || seen.has(id)) {
      continue;
    }

    seen.add(id);
    const item = getItem(itemsLookup, id);
    const list = item
      ? getList(listsLookup, item.trackable_list_id)
      : undefined;

    result.push(buildTrackableDisplay(id, item, list));
  }

  const resolved = result.filter(
    ({ itemName, listName }) => Boolean(itemName && listName)
  );

  return resolved.length > 0 ? resolved : result;
}

export function splitTrackableDisplays(displays: TrackableDisplay[]) {
  const resolved = displays.filter(
    ({ itemName, listName }) => Boolean(itemName && listName)
  );

  const primary = resolved[0] ?? displays[0];

  if (!primary) {
    return { primary: undefined as TrackableDisplay | undefined, extras: [] as TrackableDisplay[] };
  }

  const extrasSource = resolved.length
    ? resolved.slice(1)
    : displays.slice(1);

  const extras = extrasSource.filter((display) => display.id !== primary.id);

  return { primary, extras };
}

type MinimalFileSearchResult = Pick<
  FilesSearchResult,
  'file_id' | 'file_name' | 'stream_id' | 'camera_id'
>;

type TrackableSourceMaps = {
  cameras?: Map<string, Pick<Camera, 'id' | 'name'>>;
  streams?: Map<string, Pick<Stream, 'id' | 'name' | 'camera_id'>>;
  files?: Map<string, Pick<LumeoFile, 'id' | 'name' | 'description'>>;
};

export type TrackableSourceDetails = {
  sourceIcon?: 'camera' | 'stream' | 'file';
  sourceLabel?: string;
  fileLabel?: string;
};

function trimLabel(value?: string | null) {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

export function resolveTrackableSourceDetails<
  T extends MinimalFileSearchResult | undefined
>(result: T, maps: TrackableSourceMaps = {}): TrackableSourceDetails {
  if (!result) {
    return {};
  }

  const { cameras, streams, files } = maps;
  const stream = result.stream_id ? streams?.get(result.stream_id) : undefined;
  const camera =
    (result.camera_id ? cameras?.get(result.camera_id) : undefined) ??
    (stream?.camera_id ? cameras?.get(stream.camera_id) : undefined);

  let sourceIcon: TrackableSourceDetails['sourceIcon'];
  let sourceLabel = trimLabel(camera?.name);

  if (sourceLabel) {
    sourceIcon = 'camera';
  } else {
    sourceLabel = trimLabel(stream?.name);
    if (sourceLabel) {
      sourceIcon = 'stream';
    }
  }

  const file = result.file_id ? files?.get(result.file_id) : undefined;
  const fileDescription = trimLabel(file?.description);
  const fileName = trimLabel(file?.name) ?? trimLabel(result.file_name);

  if (!sourceLabel) {
    sourceLabel = fileDescription ?? fileName;
    if (!sourceIcon) {
      sourceIcon = 'file';
    }
  }

  const fileLabel = fileName;

  return {
    sourceIcon,
    sourceLabel,
    fileLabel,
  };
}
